import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../../ui/TouchTarget.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrictProgress,
  getNumberKingdomState,
  saveNumberKingdomDistrictProgress,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import { createNumberKingdomHomeButton, type NumberKingdomHomeButton } from '../../components/NumberKingdomHomeButton.js';
import { SUBTRACTION_KEEP_ASSETS } from '../subtraction-keep/SubtractionKeepAssets.js';
import {
  ROYAL_CHALLENGE_HALL_ASSETS,
  ROYAL_CHALLENGE_HALL_ASSET_LIST,
  canEnterRoyalChallengeHall,
  getMissingRoyalChallengeHallAssets,
} from './RoyalChallengeHallAssets.js';
import {
  ROYAL_CHALLENGE_HALL_TRIALS,
  formatRoyalChallengeValue,
  type RoyalChallengeHallTrialConfig,
} from './RoyalChallengeHallConfig.js';
import {
  RoyalChallengeHallState,
  type RoyalChallengeHallSerializedState,
} from './RoyalChallengeHallState.js';

export const ROYAL_CHALLENGE_HALL_SCENE_KEY = 'RoyalChallengeHall';
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
const CREST_POSITIONS = [
  { x: 105, y: 333 }, { x: 305, y: 344 }, { x: 448, y: 350 },
  { x: 850, y: 350 }, { x: 974, y: 344 }, { x: 1180, y: 333 },
] as const;

interface RoyalChallengeHallProgress {
  readonly version: 1;
  readonly trialIndex: number;
  readonly trialId: string;
  readonly trialState: RoyalChallengeHallSerializedState;
  readonly completedTrialIds: readonly string[];
  readonly restored: boolean;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class RoyalChallengeHallScene extends Phaser.Scene {
  static readonly requiredImageKeys = ROYAL_CHALLENGE_HALL_ASSET_LIST.map(asset => asset.key);

  private trialIndex = 0;
  private completedTrialIds = new Set<string>();
  private trial!: RoyalChallengeHallState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private crestText!: Phaser.GameObjects.Text;
  private valueText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private completeText!: Phaser.GameObjects.Text;
  private crestGlows: Phaser.GameObjects.Image[] = [];
  private buttons: ArtButton[] = [];
  private restored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: ROYAL_CHALLENGE_HALL_SCENE_KEY }); }

  create() {
    this.crestGlows = [];
    this.buttons = [];
    this.trialIndex = 0;
    this.completedTrialIds = new Set<string>();
    this.leaving = false;
    this.suspended = false;

    const world = getNumberKingdomState();
    const missing = getMissingRoyalChallengeHallAssets(this.textures);
    if (!world.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.masteryKeep)
      || !canEnterRoyalChallengeHall(this.textures)) {
      if (missing.length > 0) {
        console.error('[RoyalChallengeHall] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('CoinmakersVault');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.restored = world.districtCompletion[NUMBER_KINGDOM_DISTRICTS.masteryKeep] === true;
    this.restoreProgress(world.districtProgress[NUMBER_KINGDOM_DISTRICTS.masteryKeep]);
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.masteryKeep);
    SaveManager.setLastScene(ROYAL_CHALLENGE_HALL_SCENE_KEY);
    SaveManager.save();
    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.cameras.main.fadeIn(this.reducedMotion ? 140 : 420, 255, 225, 170);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<RoyalChallengeHallProgress> | undefined;
    if (progress?.version === 1) {
      this.trialIndex = Phaser.Math.Clamp(
        Number.isInteger(progress.trialIndex) ? Number(progress.trialIndex) : 0,
        0,
        ROYAL_CHALLENGE_HALL_TRIALS.length - 1,
      );
      this.completedTrialIds = new Set(
        (progress.completedTrialIds ?? []).filter(id =>
          ROYAL_CHALLENGE_HALL_TRIALS.some(config => config.id === id)),
      );
      this.restored ||= progress.restored === true;
      const config = ROYAL_CHALLENGE_HALL_TRIALS[this.trialIndex];
      this.trial = new RoyalChallengeHallState(
        config,
        progress.trialId === config.id ? progress.trialState : undefined,
      );
      return;
    }
    this.trial = new RoyalChallengeHallState(ROYAL_CHALLENGE_HALL_TRIALS[0]);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, ROYAL_CHALLENGE_HALL_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, ROYAL_CHALLENGE_HALL_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1);
    this.crestGlows = CREST_POSITIONS.map(position => this.add
      .image(position.x, position.y, ROYAL_CHALLENGE_HALL_ASSETS.masteryCrest.key)
      .setDisplaySize(94, 94).setDepth(18).setVisible(false));
    this.missionPanel = this.add.image(640, 105, SUBTRACTION_KEEP_ASSETS.missionPanel.key).setDepth(40);
    this.cosmo = this.add.image(105, 640, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key).setDepth(105);
  }

  private buildInterface() {
    this.titleText = this.makeText('ROYAL CHALLENGE HALL', 28, '#fff2ad', 'bold')
      .setStroke('#2c1746', 7).setDepth(110);
    this.missionText = this.makeText('', 16, '#ffffff', 'bold')
      .setStroke('#233a69', 5).setWordWrapWidth(700).setDepth(110);
    this.crestText = this.makeText('', 17, '#fff2ad', 'bold')
      .setStroke('#26375c', 6).setDepth(110);
    this.valueText = this.makeText('', 40, '#ffffff', 'bold')
      .setStroke('#26375c', 9).setDepth(110);
    this.feedbackText = this.makeText('', 14, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setWordWrapWidth(760).setDepth(110);
    this.controlsText = this.makeText(
      '←/→ tune • S change step • Enter check/next • H hint • R reset • Esc vault • M Home',
      10, '#fff8db', 'bold',
    ).setStroke('#26375c', 5).setDepth(110);
    this.backText = this.makeText("←  COINMAKER'S VAULT", 14, '#ffffff', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);
    this.completeText = this.makeText('NUMBER KINGDOM RESTORED • RETURN HOME', 16, '#fff2ad', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.backText, this.completeText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToVault, this);
    this.completeText.on('pointerup', this.returnHome, this);
    this.buttons = [
      this.makeButton('LESS', () => this.adjust(-1)),
      this.makeButton('STEP', () => this.cycleStep()),
      this.makeButton('MORE', () => this.adjust(1)),
      this.makeButton('CHECK', () => this.primaryAction()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.resetTrial()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI, fontSize: `${size}px`, fontStyle: style, color, align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, SUBTRACTION_KEEP_ASSETS.actionButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 13, '#ffffff', 'bold').setStroke('#26375c', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private adjust(direction: -1 | 1) {
    if (!this.canAct()) return;
    const result = this.trial.adjust(direction);
    this.feedbackText.setText(result === 'limit'
      ? 'The dial has reached its safe limit. Change direction or choose a smaller step.'
      : `The crest moved by ${this.trial.snapshot.stepSize}. You can revise it before checking.`);
    audio.click();
    this.capture();
  }

  private cycleStep() {
    if (!this.canAct()) return;
    this.trial.cycleStep();
    this.feedbackText.setText(`Tuning step changed to ${this.trial.snapshot.stepSize}.`);
    audio.click();
    this.capture();
  }

  private primaryAction() {
    if (this.restored) return this.returnHome();
    if (this.trial.snapshot.complete) return this.startNextTrial();
    const result = this.trial.check();
    if (result !== 'complete') {
      this.feedbackText.setText(result === 'too-low'
        ? 'That setting is too low. Tune upward and check again.'
        : 'That setting is too high. Tune downward and check again.');
      audio.wrong();
      return this.capture();
    }
    const config = this.currentConfig;
    this.completedTrialIds.add(config.id);
    SaveManager.recordAnswer(config.skillId, true);
    this.feedbackText.setText(`${config.crestName} restored! The hall answers your reasoning.`);
    audio.correct();
    this.playCrestReaction(this.trialIndex);
    if (this.completedTrialIds.size === ROYAL_CHALLENGE_HALL_TRIALS.length) this.completeDistrict();
    else this.buttons[3].label.setText('NEXT CREST');
    this.capture();
  }

  private startNextTrial() {
    const nextIndex = ROYAL_CHALLENGE_HALL_TRIALS.findIndex(
      config => !this.completedTrialIds.has(config.id),
    );
    if (nextIndex < 0) return this.completeDistrict();
    this.trialIndex = nextIndex;
    this.trial = new RoyalChallengeHallState(this.currentConfig);
    this.feedbackText.setText('The next royal crest turns toward the central dais.');
    this.buttons[3].label.setText('CHECK');
    this.capture();
  }

  private completeDistrict() {
    this.restored = true;
    this.completedTrialIds = new Set(ROYAL_CHALLENGE_HALL_TRIALS.map(config => config.id));
    completeNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.masteryKeep,
      this.progressSnapshot(),
    );
    this.feedbackText.setText('All six crests shine. The crown portal opens and Number Kingdom celebrates!');
    this.playMagicBurst(640, 360, 620);
    audio.restoration();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.trial.requestHint() ?? 'Break the problem into smaller steps.');
    audio.click();
    this.capture();
  }

  private resetTrial() {
    if (!this.canAct()) return;
    this.trial = new RoyalChallengeHallState(this.currentConfig);
    this.feedbackText.setText('This crest is reset. Previously restored crests remain safe.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.masteryKeep, this.progressSnapshot());
    this.refresh();
  }

  private progressSnapshot(): RoyalChallengeHallProgress {
    return {
      version: 1,
      trialIndex: this.trialIndex,
      trialId: this.currentConfig.id,
      trialState: this.trial.serialize(),
      completedTrialIds: [...this.completedTrialIds],
      restored: this.restored,
    };
  }

  private refresh() {
    const snapshot = this.trial.snapshot;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.crestGlows.forEach((crest, index) => crest.setVisible(
      !this.restored && this.completedTrialIds.has(ROYAL_CHALLENGE_HALL_TRIALS[index].id),
    ));
    this.missionText.setText(this.restored
      ? 'The crown portal is open. Every district remembers the work that restored it.'
      : `Trial ${this.trialIndex + 1} of ${ROYAL_CHALLENGE_HALL_TRIALS.length}: ${this.currentConfig.prompt}`);
    this.crestText.setText(this.restored ? 'ALL SIX CRESTS RESTORED' : this.currentConfig.crestName);
    this.valueText.setText(this.restored
      ? 'NUMBER KINGDOM COMPLETE'
      : formatRoyalChallengeValue(snapshot.value, this.currentConfig.format));
    this.buttons.forEach(button => {
      button.image.setVisible(!this.restored);
      button.label.setVisible(!this.restored);
    });
    this.buttons[1].label.setText(`STEP ${snapshot.stepSize}`);
    this.buttons[3].label.setText(snapshot.complete ? 'NEXT CREST' : 'CHECK');
    this.controlsText.setVisible(!this.restored);
    this.completeText.setVisible(this.restored);
    if (!this.feedbackText.text) {
      this.feedbackText.setText('Tune the crest with meaningful steps. Every setting can be revised.');
    }
    this.layout();
  }

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    const portrait = safe.width < 700;
    const panelWidth = portrait ? safe.width - 24 : Math.min(compact ? 680 : 790, safe.width - 210);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 132 : compact ? 88 : 100))
      .setDisplaySize(panelWidth, panelWidth * (360 / 900));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 57 : 25))
      .setFontSize(portrait ? 20 : compact ? 23 : 28);
    this.missionText.setPosition(safe.centerX, safe.y + (portrait ? 132 : compact ? 91 : 106))
      .setFontSize(portrait ? 12 : compact ? 13 : 16).setWordWrapWidth(panelWidth * 0.82);
    this.backText.setPosition(safe.x + (portrait ? 238 : compact ? 250 : 282), safe.y + 30)
      .setFontSize(portrait ? 8 : compact ? 11 : 14);
    this.homeButton.layout(safe, compact || portrait, 'left');
    this.crestText.setPosition(safe.centerX, safe.y + (portrait ? 231 : compact ? 186 : 214))
      .setFontSize(portrait ? 13 : compact ? 15 : 17);
    this.valueText.setPosition(safe.centerX, safe.y + (portrait ? 285 : compact ? 244 : 277))
      .setFontSize(this.restored ? (portrait ? 24 : 34) : (portrait ? 34 : compact ? 37 : 44));

    const columns = portrait ? 2 : 3;
    const rows = Math.ceil(this.buttons.length / columns);
    const buttonWidth = portrait ? Math.min(142, safe.width / 2 - 18) : compact ? 132 : 150;
    const buttonHeight = portrait ? 55 : compact ? 61 : 68;
    const gapX = buttonWidth + (portrait ? 9 : 18);
    const gapY = buttonHeight + 8;
    const startY = safe.bottom - (rows * gapY + (portrait ? 52 : 55));
    this.buttons.forEach((button, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const rowCount = Math.min(columns, this.buttons.length - row * columns);
      const x = safe.centerX + (column - (rowCount - 1) / 2) * gapX;
      const y = startY + row * gapY;
      button.image.setPosition(x, y).setDisplaySize(buttonWidth, buttonHeight);
      button.label.setPosition(x, y).setFontSize(portrait ? 10 : compact ? 11 : 13);
    });
    this.feedbackText.setPosition(safe.centerX, startY - (portrait ? 42 : 48))
      .setFontSize(portrait ? 11 : compact ? 12 : 14)
      .setWordWrapWidth(portrait ? safe.width - 36 : Math.min(760, safe.width - 210));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 16)
      .setFontSize(portrait ? 7 : compact ? 9 : 10).setWordWrapWidth(safe.width - 20);
    this.completeText.setPosition(safe.centerX, safe.bottom - (portrait ? 70 : 52))
      .setFontSize(portrait ? 12 : 16);
    this.cosmo.setPosition(safe.x + (compact ? 53 : 72), safe.bottom - (compact ? 46 : 58))
      .setDisplaySize(compact ? 80 : 104, compact ? 72 : 93);
  }

  private playCrestReaction(index: number) {
    const crest = this.crestGlows[index].setVisible(true);
    if (this.reducedMotion) return crest.setAlpha(0.9);
    crest.setAlpha(0).setScale(0.16);
    this.tweens.add({ targets: crest, alpha: 1, scaleX: 0.245, scaleY: 0.245, duration: 440 });
  }

  private playMagicBurst(x: number, y: number, size: number) {
    const burst = this.add.image(x, y, SUBTRACTION_KEEP_ASSETS.magicBurst.key)
      .setDisplaySize(size, size).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => burst.destroy());
    else this.tweens.add({ targets: burst, alpha: { from: 0, to: 0.9 }, scaleX: 1.12, scaleY: 1.12,
      duration: 500, yoyo: true, hold: 120, onComplete: () => burst.destroy() });
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToVault();
    if (key === 'm') return this.returnHome();
    if (key === 'enter' || key === ' ') return this.primaryAction();
    if (key === 'arrowleft' || key === '-') return this.adjust(-1);
    if (key === 'arrowright' || key === '+') return this.adjust(1);
    if (key === 's') return this.cycleStep();
    if (key === 'h') return this.hint();
    if (key === 'r') this.resetTrial();
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private get currentConfig(): RoyalChallengeHallTrialConfig {
    return ROYAL_CHALLENGE_HALL_TRIALS[this.trialIndex];
  }

  private canAct() {
    return !this.leaving && !this.suspended && !this.restored && !this.trial.snapshot.complete;
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private readonly returnToVault = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.masteryKeep, this.progressSnapshot());
    this.scene.start('CoinmakersVault');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.masteryKeep, this.progressSnapshot());
    SaveManager.setLastScene(ROYAL_CHALLENGE_HALL_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private shutdown() {
    if (this.trial) {
      saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.masteryKeep, this.progressSnapshot());
    }
    this.unsubscribeLifecycle?.();
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.tweens.killAll();
  }
}
